import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  App as AntdApp,
  Button,
  Card,
  Dropdown,
  Result,
  Space,
  Typography,
} from 'antd'
import {
  ArrowLeftOutlined,
  AuditOutlined,
  CommentOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SaveOutlined,
} from '@ant-design/icons'
import { supabase } from '@/lib/supabase'
import { formatCST } from '@/lib/time'
import { useIsMobile } from '@/lib/hooks'
import { DeleteAnimeAction } from '@/components/DeleteAnimeAction'
import { AnimeEditForm } from './-anime-edit'
import type { AnimeRow, EditPatch } from './-anime-edit'

export const Route = createFileRoute('/_authed/anime_/$animeId/edit')({
  component: AnimeEditPage,
})

const FORM_ID = 'anime-edit-form'

function AnimeEditPage() {
  const { animeId } = Route.useParams()
  const id = Number(animeId)
  const validId = Number.isSafeInteger(id) && id > 0
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const qc = useQueryClient()
  const { message } = AntdApp.useApp()

  const animeQuery = useQuery({
    queryKey: ['animes', 'detail', id],
    enabled: validId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('animes')
        .select('*, anime_types(id, name), anime_series(id, title)')
        .eq('id', id)
        .single()
      if (error) throw error
      return data as unknown as AnimeRow
    },
  })

  const typesQuery = useQuery({
    queryKey: ['anime_types', 'all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('anime_types')
        .select('id, name')
        .order('sort_order', { ascending: true, nullsFirst: false })
        .order('name', { ascending: true })
      if (error) throw error
      return data ?? []
    },
    staleTime: 5 * 60_000,
  })

  const seriesQuery = useQuery({
    queryKey: ['anime_series', 'all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('anime_series')
        .select('id, title')
        .order('title', { ascending: true })
      if (error) throw error
      return data ?? []
    },
    staleTime: 5 * 60_000,
  })

  const updateMutation = useMutation({
    mutationFn: async (patch: EditPatch) => {
      const { error } = await supabase.from('animes').update(patch).eq('id', id)
      if (error) throw error
    },
    onSuccess: async () => {
      message.success('已保存')
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['animes'] }),
        animeQuery.refetch(),
      ])
    },
    onError: (e: Error) => {
      message.error(e.message || '保存失败')
    },
  })

  if (!validId) {
    return (
      <Result
        status="404"
        title="番剧 ID 无效"
        extra={
          <Button onClick={() => navigate({ to: '/anime' })}>返回列表</Button>
        }
      />
    )
  }

  if (animeQuery.isError) {
    return (
      <Result
        status="error"
        title="番剧加载失败"
        subTitle={(animeQuery.error as Error).message}
        extra={
          <Space>
            <Button onClick={() => navigate({ to: '/anime' })}>返回列表</Button>
            <Button type="primary" onClick={() => animeQuery.refetch()}>
              重试
            </Button>
          </Space>
        }
      />
    )
  }

  const row = animeQuery.data

  return (
    <div className="admin-page">
      <div className="admin-sticky-toolbar">
        <div className="admin-toolbar-title">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate({ to: '/anime' })}
            aria-label="返回列表"
          />
          <div style={{ minWidth: 0 }}>
            <div className="admin-toolbar-title__main">
              {row ? `编辑 · ${row.title}` : '编辑番剧'}
            </div>
            {row ? (
              <div className="admin-toolbar-title__meta">
                <Typography.Text
                  type="secondary"
                  copyable={{
                    text: String(row.id),
                    tooltips: ['复制 ID', '已复制'],
                  }}
                  style={{ fontSize: 12 }}
                >
                  ID {row.id}
                </Typography.Text>
                <span> · 上次保存 {formatCST(row.updated_at)}</span>
              </div>
            ) : null}
          </div>
        </div>

        {row && isMobile ? (
          // 手机：主操作「保存」+ 剧集露出，评论 / 审计收进「更多」，删除只留图标
          <Space className="anime-edit-toolbar-actions">
            <Dropdown
              trigger={['click']}
              disabled={updateMutation.isPending}
              menu={{
                items: [
                  { key: 'comments', icon: <CommentOutlined />, label: '评论' },
                  { key: 'audit', icon: <AuditOutlined />, label: '审计' },
                ],
                onClick: ({ key }) => {
                  if (key === 'comments')
                    navigate({ to: '/comments', search: { anime_id: row.id } })
                  else
                    navigate({
                      to: '/audit',
                      search: {
                        target_type: 'anime',
                        target_id: String(row.id),
                      },
                    })
                },
              }}
            >
              <Button icon={<MoreOutlined />} aria-label="更多操作" />
            </Dropdown>
            <DeleteAnimeAction
              animeId={row.id}
              animeTitle={row.title}
              mode="button"
              iconOnly
              onDeleted={() => navigate({ to: '/anime' })}
            />
            <Button
              icon={<OrderedListOutlined />}
              onClick={() =>
                navigate({
                  to: '/anime/$animeId/episodes',
                  params: { animeId: String(row.id) },
                })
              }
              disabled={updateMutation.isPending}
            >
              剧集
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              htmlType="submit"
              form={FORM_ID}
              loading={updateMutation.isPending}
            >
              保存
            </Button>
          </Space>
        ) : row ? (
          <Space wrap>
            <Button
              icon={<OrderedListOutlined />}
              onClick={() =>
                navigate({
                  to: '/anime/$animeId/episodes',
                  params: { animeId: String(row.id) },
                })
              }
              disabled={updateMutation.isPending}
            >
              剧集
            </Button>
            <Button
              icon={<CommentOutlined />}
              onClick={() =>
                navigate({ to: '/comments', search: { anime_id: row.id } })
              }
              disabled={updateMutation.isPending}
            >
              评论
            </Button>
            <Button
              icon={<AuditOutlined />}
              onClick={() =>
                navigate({
                  to: '/audit',
                  search: { target_type: 'anime', target_id: String(row.id) },
                })
              }
              disabled={updateMutation.isPending}
            >
              审计
            </Button>
            <DeleteAnimeAction
              animeId={row.id}
              animeTitle={row.title}
              mode="button"
              onDeleted={() => navigate({ to: '/anime' })}
            />
            <Button
              type="primary"
              icon={<SaveOutlined />}
              htmlType="submit"
              form={FORM_ID}
              loading={updateMutation.isPending}
            >
              保存
            </Button>
          </Space>
        ) : null}
      </div>

      {row ? (
        <AnimeEditForm
          row={row}
          formId={FORM_ID}
          onSave={(patch) => updateMutation.mutate(patch)}
          types={typesQuery.data ?? []}
          typesLoading={typesQuery.isLoading}
          series={seriesQuery.data ?? []}
          seriesLoading={seriesQuery.isLoading}
        />
      ) : (
        <Card loading />
      )}
    </div>
  )
}
